// Packet proposal progress and review (blueprint-sets Req 9.6, 10; design "pages/SetProposalPage.tsx").
// Polls `GET /set-proposals/:runId` (`useSetProposal`) until the proposal settles. The run continues on the server
// while the author is away; coming back to this URL resumes from the server's state (Req 9.6).
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import ProgressBar, { type ProgressBarProps } from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import type { setProposalStatusResponse } from '@bda/shared';
import type { z } from 'zod';

import { api } from '../../api/client';
import { useSetProposal } from '../../api/hooks';
import { defaultSetName, SetProposalReview } from './SetProposalReview';
import { useSetProposalPreview } from './SetProposalPreview';
import { errorMessage } from '../../lib/notify';

type ProposalStatus = z.infer<typeof setProposalStatusResponse>;

export const BDA_STAGE_LABEL = 'BDA is reading the packet';
export const classifyLabel = (done: number | null, total: number | null): string =>
  done !== null && total !== null ? `Classifying pages (${done} of ${total})` : 'Classifying pages';
export const proposeLabel = (done: number | null, total: number | null): string =>
  done !== null && total !== null ? `Proposing blueprints (${done} of ${total})` : 'Proposing blueprints';

interface Stage {
  label: string;
  value: number;
  status: ProgressBarProps.Status;
  description?: string;
}

const pct = (done: number | null, total: number | null): number =>
  done === null || total === null || total <= 0 ? 0 : Math.max(0, Math.min(100, Math.round((done / total) * 100)));

/** The three stage bars for a proposal that is not settled (Req 9.6). */
export function proposalStages(data: ProposalStatus): Stage[] {
  const { run, proposal } = data;
  const ps = proposal.status;
  const bdaDone = run.status === 'SUCCEEDED';
  const classifying = ps === 'CLASSIFYING';
  const proposing = ps === 'PROPOSING';
  const classified = proposing || ps === 'READY';
  return [
    bdaDone
      ? {
          label: BDA_STAGE_LABEL,
          value: 100,
          status: 'success',
          description: run.pages ? `${run.pages} pages read.` : undefined,
        }
      : {
          label: BDA_STAGE_LABEL,
          value: 0,
          status: 'in-progress',
          description:
            run.status === 'UPLOADING'
              ? 'Waiting for the upload to finish.'
              : 'BDA processes every page; a long packet can take several minutes.',
        },
    classifying
      ? {
          label: classifyLabel(proposal.done, proposal.total),
          value: pct(proposal.done, proposal.total),
          status: 'in-progress',
        }
      : {
          label: classifyLabel(null, null),
          value: classified ? 100 : 0,
          status: classified ? 'success' : 'in-progress',
        },
    proposing
      ? {
          label: proposeLabel(proposal.done, proposal.total),
          value: pct(proposal.done, proposal.total),
          status: 'in-progress',
        }
      : {
          label: proposeLabel(null, null),
          value: ps === 'READY' ? 100 : 0,
          status: ps === 'READY' ? 'success' : 'in-progress',
        },
  ];
}

const failureOf = (data: ProposalStatus): string | null => {
  if (data.run.status === 'FAILED') return data.run.failureReason ?? 'BDA could not process the packet.';
  if (data.proposal.status === 'FAILED') return data.proposal.failure ?? 'The proposal could not be built.';
  return null;
};

export function SetProposalPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const q = useSetProposal(runId);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  // "Preview set" (Req 11): the preview state lives here so it survives the review's re-renders.
  const setPreview = useSetProposalPreview();
  // Keep the first document URL: refetches return a new presigned URL, which would reload every thumbnail.
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const fresh = q.data?.documentUrl ?? null;
  if (docUrl === null && fresh) setDocUrl(fresh);

  if (q.error) return <Alert type="error">{errorMessage(q.error)}</Alert>;
  if (!q.data) return <Spinner size="large" />;
  const data = q.data;
  const { run, proposal, result } = data;
  const failure = failureOf(data);

  const retry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const next = await api.retryRun(run.id);
      navigate(`/sets/proposals/${encodeURIComponent(next.id)}`);
    } catch (e) {
      setRetryError(errorMessage(e));
    } finally {
      setRetrying(false);
    }
  };

  let content;
  if (failure) {
    content = (
      <Alert
        type="error"
        header="The proposal failed"
        action={
          !run.expired && (
            <Button loading={retrying} onClick={() => void retry()}>
              Try again
            </Button>
          )
        }
      >
        <SpaceBetween size="xs">
          <span>{failure}</span>
          {run.expired && <span>The sample packet has expired. Upload it again to start a new proposal.</span>}
          {retryError && <Box color="text-status-error">{retryError}</Box>}
        </SpaceBetween>
      </Alert>
    );
  } else if (proposal.status === 'READY' && result) {
    content = (
      <SetProposalReview
        proposal={result}
        existingMatches={data.existingMatches ?? []}
        document={docUrl ? { url: docUrl, contentType: run.contentType, name: run.fileName } : null}
        defaultName={defaultSetName(run.fileName)}
        preview={setPreview}
      />
    );
  } else if (proposal.status === 'READY') {
    content = (
      <Alert type="warning">
        The sample packet and its proposal have expired. Upload it again to start a new proposal.
      </Alert>
    );
  } else {
    content = (
      <Container header={<Header variant="h2">Building the proposal</Header>}>
        <SpaceBetween size="l">
          {proposalStages(data).map((s) => (
            <ProgressBar
              key={s.label.split(' (')[0]}
              label={s.label}
              value={s.value}
              status={s.status}
              description={s.description}
              resultText="Done"
            />
          ))}
          <Box color="text-body-secondary">
            This continues if you leave the page. Come back to this page to see the proposal; nothing is created until
            you review it.
          </Box>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={run.fileName}>
          Blueprint set proposal
        </Header>
      }
    >
      {content}
    </ContentLayout>
  );
}
